/**
 * Confronto armonizzati/ordinari (% persone/costo + scarto costo medio
 * orario) — GET /costo-personale/confronto-armonizzazione. Segue sempre gli
 * stessi filtri della pagina; la dimensione di scomposizione è il groupBy
 * corrente quando è una delle 3 dimensioni supportate (mansione, centro di
 * costo, gruppo mansioni), altrimenti un unico blocco "globale".
 */
import React, { useMemo } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, Spinner } from "../../components/ui/basics";
import { euro } from "./format";

const DIMENSIONI_VALIDE = ["mansioneCodice", "centroCosto", "gruppoCosto"];
const LABEL_DIMENSIONE = {
  mansioneCodice: "per mansione",
  centroCosto: "per centro di costo",
  gruppoCosto: "per gruppo mansioni",
};

function pct(v) {
  return v == null
    ? "—"
    : `${v.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;
}

export default function ConfrontoArmonizzazionePanel({
  filtriCorrente,
  groupBy,
  mansioniCatalogo,
}) {
  const dimensione = DIMENSIONI_VALIDE.includes(groupBy) ? groupBy : "globale";

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (filtriCorrente.periodoDa) p.set("periodoDa", filtriCorrente.periodoDa);
    if (filtriCorrente.periodoA) p.set("periodoA", filtriCorrente.periodoA);
    if (filtriCorrente.rsa) p.set("rsa", filtriCorrente.rsa);
    if (filtriCorrente.mansione) p.set("mansione", filtriCorrente.mansione);
    if (filtriCorrente.centroCosto)
      p.set("centroCosto", filtriCorrente.centroCosto);
    if (filtriCorrente.gruppo) p.set("gruppo", filtriCorrente.gruppo);
    if (filtriCorrente.codiceFiscale)
      p.set("codiceFiscale", filtriCorrente.codiceFiscale);
    p.set("dimensione", dimensione);
    return p.toString();
  }, [filtriCorrente, dimensione]);

  const { data, loading, error } = useApi(
    `/costo-personale/confronto-armonizzazione?${query}`,
    [query],
  );

  const nomeMansione = useMemo(
    () => new Map((mansioniCatalogo || []).map((m) => [m.codice, m.nome])),
    [mansioniCatalogo],
  );

  if (loading) return <Spinner label="Calcolo confronto…" />;
  if (error || !data?.righe?.length) return null;

  return (
    <Card
      title="Armonizzati vs Ordinari"
      subtitle={`Assunto prima/dopo l'avvio della RSA in Peralba — ${dimensione === "globale" ? "vista globale" : LABEL_DIMENSIONE[dimensione]}`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              {dimensione !== "globale" && (
                <th className="py-1 px-3">Chiave</th>
              )}
              <th className="py-1 px-3 text-right">Armonizzati (n)</th>
              <th className="py-1 px-3 text-right">Ordinari (n)</th>
              <th className="py-1 px-3 text-right">% persone armonizzati</th>
              <th className="py-1 px-3 text-right">% costo armonizzati</th>
              <th className="py-1 px-3 text-right">€/ora armonizzati</th>
              <th className="py-1 px-3 text-right">€/ora ordinari</th>
              <th className="py-1 px-3 text-right">Scarto €/ora</th>
            </tr>
          </thead>
          <tbody>
            {data.righe.map((r) => (
              <tr key={r.chiave} className="border-b border-gray-100">
                {dimensione !== "globale" && (
                  <td className="py-1 px-3">
                    {r.nome || nomeMansione.get(r.chiave) || r.chiave}
                  </td>
                )}
                <td className="py-1 px-3 text-right">
                  {r.armonizzato.dipendenti}
                </td>
                <td className="py-1 px-3 text-right">
                  {r.ordinario.dipendenti}
                </td>
                <td className="py-1 px-3 text-right">
                  {pct(r.percentualeArmonizzatiPersone)}
                </td>
                <td className="py-1 px-3 text-right">
                  {pct(r.percentualeArmonizzatiCosto)}
                </td>
                <td className="py-1 px-3 text-right">
                  {r.armonizzato.costoMedioOrario != null
                    ? euro(r.armonizzato.costoMedioOrario)
                    : "—"}
                </td>
                <td className="py-1 px-3 text-right">
                  {r.ordinario.costoMedioOrario != null
                    ? euro(r.ordinario.costoMedioOrario)
                    : "—"}
                </td>
                <td
                  className={`py-1 px-3 text-right font-medium ${
                    r.scartoCostoOrarioPct == null
                      ? "text-gray-400"
                      : r.scartoCostoOrarioPct > 0
                        ? "text-red-600"
                        : "text-green-700"
                  }`}
                >
                  {r.scartoCostoOrarioPct == null
                    ? "—"
                    : `${r.scartoCostoOrarioPct > 0 ? "+" : ""}${r.scartoCostoOrarioPct.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Scarto €/ora: positivo = gli armonizzati costano di più all'ora degli
        ordinari, negativo = costano meno.
      </p>
    </Card>
  );
}
